import { c as _c } from "react/compiler-runtime";
import { Hidden } from '@/components/Atoms/Hidden/Hidden';
import { useAppContext } from '@/contexts/AppContext/AppContext';
import useTranslation from '@/hooks/useTranslation';
import type { IAnalyticsTraceableElement, IFormChoice } from '@/interfaces';
import { trackEvent } from '@/services/Analytics/BBAnalytics';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import clientStorage from '@/services/ClientStorage';
import { extractSearchParams } from '@/utils/siteUtils';
import type { TranslationDetails } from 'bnef';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import styles from './ContentTranslation.module.css';
const PREFERRED_LANGUAGE_STORAGE_KEY = ['translations', 'locale'];
export const LANGUAGE_PARAM_KEY = 'language';

/**
 * A component to enable the selection of different locales.
 */
export const ContentTranslation: FC<IContentTranslationProps> = t0 => {
  const $ = _c(87);
  const {
    originalContentLanguage,
    currentTranslationDetails,
    targetDestination,
    languageOptions,
    disclaimerId
  } = t0;
  const {
    t
  } = useTranslation("common");
  const {
    user
  } = useAppContext();
  const [currentTranslationTarget, setCurrentTranslationTarget] = useState("");
  let t1;
  if ($[0] !== languageOptions || $[1] !== originalContentLanguage) {
    let t2;
    if ($[3] !== originalContentLanguage) {
      t2 = option => option.value !== originalContentLanguage;
      $[3] = originalContentLanguage;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = languageOptions.filter(t2);
    $[0] = languageOptions;
    $[1] = originalContentLanguage;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const options = t1;
  let t2;
  let t3;
  if ($[5] !== options || $[6] !== user) {
    t2 = () => {
      const isAvailable = locale => options.some(option_0 => option_0.value === locale);
      const preferredLanguage = clientStorage.get(PREFERRED_LANGUAGE_STORAGE_KEY);
      if (preferredLanguage && isAvailable(preferredLanguage)) {
        setCurrentTranslationTarget(preferredLanguage);
      } else {
        if (user.l10n && isAvailable(user.l10n)) {
          setCurrentTranslationTarget(user.l10n);
        }
      }
    };
    t3 = [user, options];
    $[5] = options;
    $[6] = user;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  const isInTranslatedView = currentTranslationDetails && originalContentLanguage !== currentTranslationDetails.language;
  if (!isInTranslatedView && options.length === 0) {
    return null;
  }
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let urlParams;
  if ($[9] !== currentTranslationTarget || $[10] !== t || $[11] !== targetDestination) {
    urlParams = extractSearchParams(targetDestination);
    let t9;
    if ($[18] !== t) {
      t9 = t("ContentTranslation.translation_is_available");
      $[18] = t;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    if ($[20] !== t9) {
      t8 = <p>{t9}</p>;
      $[20] = t9;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    t4 = "get";
    t5 = targetDestination;
    if ($[22] !== currentTranslationTarget) {
      t6 = () => {
        if (currentTranslationTarget) {
          clientStorage.set(PREFERRED_LANGUAGE_STORAGE_KEY, currentTranslationTarget);
        }
        trackEvent(TrackingAction.Translate, TrackingCategory.BnefContent, currentTranslationTarget);
      };
      $[22] = currentTranslationTarget;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    t7 = urlParams && Array.from(urlParams.entries()).map(_temp);
    $[9] = currentTranslationTarget;
    $[10] = t;
    $[11] = targetDestination;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = urlParams;
  } else {
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
    urlParams = $[17];
  }
  let t9;
  if ($[24] !== t) {
    t9 = t("Labels.language");
    $[24] = t;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t9) {
    t10 = <Hidden as="label" htmlFor="language-select">{t9}</Hidden>;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = event => setCurrentTranslationTarget(event.currentTarget.value);
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t) {
    t12 = t("Labels.language");
    $[29] = t;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12) {
    t13 = <option disabled={true} value="">{t12}</option>;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== options) {
    t14 = options.map(_temp2);
    $[33] = options;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== currentTranslationTarget || $[36] !== t13 || $[37] !== t14) {
    t15 = <select id="language-select" className={styles.select} name={LANGUAGE_PARAM_KEY} onChange={t11} value={currentTranslationTarget}>{t13}{t14}</select>;
    $[35] = currentTranslationTarget;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const t16 = !currentTranslationTarget;
  let t17;
  if ($[39] !== t) {
    t17 = t("ContentTranslation.translate");
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t16 || $[42] !== t17) {
    t18 = <button type="submit" disabled={t16}>{t17}</button>;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t10 || $[45] !== t15 || $[46] !== t18 || $[47] !== t4 || $[48] !== t5 || $[49] !== t6 || $[50] !== t7) {
    t19 = <form method={t4} action={t5} onSubmit={t6}>{t7}{t10}{t15}{t18}</form>;
    $[44] = t10;
    $[45] = t15;
    $[46] = t18;
    $[47] = t4;
    $[48] = t5;
    $[49] = t6;
    $[50] = t7;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t19 || $[53] !== t8) {
    t20 = <>{t8}{t19}</>;
    $[52] = t19;
    $[53] = t8;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  const TranslationView = t20;
  let translationInfo;
  if (currentTranslationDetails?.isAutomaticProvider) {
    if (currentTranslationDetails.translationProviderLabel) {
      let t21;
      if ($[55] !== currentTranslationDetails.translationProviderLabel || $[56] !== t) {
        t21 = t("ContentTranslation.automatic_translation_info", {
          provider: currentTranslationDetails.translationProviderLabel
        });
        $[55] = currentTranslationDetails.translationProviderLabel;
        $[56] = t;
        $[57] = t21;
      } else {
        t21 = $[57];
      }
      translationInfo = t21;
    } else {
      let t21;
      if ($[58] !== t) {
        t21 = t("ContentTranslation.automatic_translation_info_unknown_vendor");
        $[58] = t;
        $[59] = t21;
      } else {
        t21 = $[59];
      }
      translationInfo = t21;
    }
  } else {
    let t21;
    if ($[60] !== t) {
      t21 = t("ContentTranslation.default_translation_info");
      $[60] = t;
      $[61] = t21;
    } else {
      t21 = $[61];
    }
    translationInfo = t21;
  }
  let t21;
  if ($[62] !== currentTranslationDetails?.isAutomaticProvider || $[63] !== disclaimerId || $[64] !== translationInfo) {
    t21 = currentTranslationDetails?.isAutomaticProvider ? <a href={`#${disclaimerId}`}>{translationInfo}</a> : translationInfo;
    $[62] = currentTranslationDetails?.isAutomaticProvider;
    $[63] = disclaimerId;
    $[64] = translationInfo;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  const translationInfoNode = t21;
  let t22;
  if ($[66] !== translationInfoNode) {
    t22 = <p>{translationInfoNode}</p>;
    $[66] = translationInfoNode;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== urlParams) {
    t23 = urlParams && Array.from(urlParams.entries()).filter(_temp4).map(_temp5);
    $[68] = urlParams;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== originalContentLanguage) {
    t24 = <input type="hidden" name={LANGUAGE_PARAM_KEY} value={originalContentLanguage} />;
    $[70] = originalContentLanguage;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== t) {
    t25 = t("ContentTranslation.view_original");
    $[72] = t;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  let t26;
  if ($[74] !== t25) {
    t26 = <button type="submit">{t25}</button>;
    $[74] = t25;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  let t27;
  if ($[76] !== t23 || $[77] !== t24 || $[78] !== t26 || $[79] !== targetDestination) {
    t27 = <form method="get" action={targetDestination} onSubmit={_temp3}>{t23}{t24}{t26}</form>;
    $[76] = t23;
    $[77] = t24;
    $[78] = t26;
    $[79] = targetDestination;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  let t28;
  if ($[81] !== t22 || $[82] !== t27) {
    t28 = <>{t22}{t27}</>;
    $[81] = t22;
    $[82] = t27;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  const TranslatedView = t28;
  const t29 = isInTranslatedView && TranslatedView;
  const t30 = !isInTranslatedView && TranslationView;
  let t31;
  if ($[84] !== t29 || $[85] !== t30) {
    t31 = <div className={styles.container}>{t29}{t30}</div>;
    $[84] = t29;
    $[85] = t30;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  return t31;
};
interface IContentTranslationProps extends IAnalyticsTraceableElement {
  originalContentLanguage: string;
  currentTranslationDetails?: TranslationDetails;
  targetDestination: string;
  languageOptions: IFormChoice[];
  disclaimerId: string;
}
function _temp(t0) {
  const [param, value] = t0;
  return <input type="hidden" key={param} name={param} value={value as string} />;
}
function _temp2(option_1) {
  return <option key={option_1.value} value={option_1.value}>{option_1.label}</option>;
}
function _temp3() {
  trackEvent(TrackingAction.Translate, TrackingCategory.BnefContent, "original");
}
function _temp4(t0) {
  const [param_0] = t0;
  return param_0 !== LANGUAGE_PARAM_KEY;
}
function _temp5(t0) {
  const [param_1, value_0] = t0;
  return <input type="hidden" key={param_1} name={param_1} value={value_0 as string} />;
}